import { EventType } from '@graphql/generated';

import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventFeatureFlags } from './useEventFeatureFlags/useEventFeatureFlags';

export enum EventFeature {
  // use pro messaging with non-wedding email templates
  messaging,
  // use pro icon navigation in hamburger
  menu,
  // enable access to carbon integration
  carbonDashboard,
  // use the new pro admin dashboard
  proDashboard,
  // enable registry in settings and navigation and UX cross sell
  registry,
  // enable stationery in navigation and UX cross sell
  stationery,
  // use the new event admin invite workflow that has no legacy dependencies
  adminInvite,
  // Enables setting the website preview photo from general settings
  websitePhotoSetting,
  // Wedding websites have special logic around the about page always being first
  // General events are all custom pages and can have any page first
  reorderAllWebsitePages,
  // disables the overview onboarding carousel in the website overview page
  hideWebOverviewCarousel,
  // Use the pro version of the website theme gallery which removes wedding designs and imagery
  websiteThemes,
  // Expose the Pro version of website custom components
  websiteComponents,
  // General events do not use firebase or legacy API.  Attempting to sync contacts with firebase will throw errors
  skipContactFirebaseSync,
  // Disables the floating action bar identifying a user as a guest and asking them to RSVP via legacy RSVP
  disableWebActionBar,
  // disable UX that exposes links to legacy RSVP route and behavior
  disableLegacyRsvp,
  // Use pro website header config to avoid wedding content
  websiteHeaderConfig,
  // disable the website theme popover to hide wedding specific content
  disableWebThemePopover,
  // disable settings and features that are reliant on legacy system
  disableLegacyWebFeatures,
  // Disables wedding specific partner 1 and partner 2 name inputs in settings
  disableSettingPersonalInfo,
  // Disables showing the website page Add App
  disableAddAppPage,
}

type FeatureEvaluatorArgs = {
  readonly isGeneral: boolean;
  readonly isCarbonEnabled: boolean;
  readonly isModernNav: boolean;
};

const anyOptionTrue = ({ isGeneral, isCarbonEnabled, isModernNav }: FeatureEvaluatorArgs) => isGeneral || isCarbonEnabled || isModernNav;

const featureEvaluators: Readonly<Record<EventFeature, (args: FeatureEvaluatorArgs) => boolean>> = {
  [EventFeature.messaging]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.menu]: args => anyOptionTrue(args),
  [EventFeature.carbonDashboard]: ({ isCarbonEnabled }) => isCarbonEnabled,
  [EventFeature.proDashboard]: args => anyOptionTrue(args),
  [EventFeature.registry]: ({ isGeneral, isCarbonEnabled }) => !isGeneral && !isCarbonEnabled,
  [EventFeature.stationery]: ({ isGeneral, isCarbonEnabled }) => !isGeneral && !isCarbonEnabled,
  [EventFeature.adminInvite]: args => anyOptionTrue(args),
  [EventFeature.websitePhotoSetting]: args => anyOptionTrue(args),
  [EventFeature.reorderAllWebsitePages]: ({ isGeneral }) => isGeneral,
  [EventFeature.hideWebOverviewCarousel]: args => anyOptionTrue(args),
  [EventFeature.websiteThemes]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.websiteComponents]: args => anyOptionTrue(args),
  [EventFeature.skipContactFirebaseSync]: ({ isGeneral }) => isGeneral,
  [EventFeature.disableWebActionBar]: ({ isGeneral }) => isGeneral,
  [EventFeature.disableLegacyRsvp]: ({ isGeneral }) => isGeneral,
  [EventFeature.websiteHeaderConfig]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.disableWebThemePopover]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.disableLegacyWebFeatures]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.disableSettingPersonalInfo]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.disableAddAppPage]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
};

const evaluateFeature = (feature: EventFeature, isGeneral: boolean, isCarbonEnabled: boolean, isModernNav: boolean) => {
  const evaluator = featureEvaluators[feature];
  if (!evaluator) {
    return false;
  }

  return evaluator({ isGeneral, isCarbonEnabled, isModernNav });
};

/**
 * Determines whether the provided event feature should be enabled for the current event.
 * @param feature The feature flag to evaluate using event type and remote feature flags.
 */
export const useProFeature = (feature: EventFeature): boolean => {
  const { eventInfo, eventHandle } = useEventInfo();
  const eventType = eventInfo?.eventType;

  const { loadingEventFeatureFlags, isEventFeatureFlagEnabled } = useEventFeatureFlags({ eventHandle: eventHandle ?? '', skip: !eventHandle });
  const isCarbonEnabled = loadingEventFeatureFlags ? false : isEventFeatureFlagEnabled?.('useCarbon') ?? false;
  const useModernNavigation = loadingEventFeatureFlags ? false : isEventFeatureFlagEnabled?.('useModernNavigation') ?? false;
  const isGeneral = eventType === EventType.general;

  return evaluateFeature(feature, isGeneral, isCarbonEnabled, useModernNavigation);
};
